import React, { useState } from 'react';
import classnames from 'classnames';
import * as styles from './card-select-modal.less';
import { CreditCard, CreditCardIssuer } from 'src/api';
import {
    Modal,
    Button,
    ButtonGroup,
    Card,
    Stack,
    BodyText,
    Tag,
    Radio,
    Link,
} from '@servicetitan/design-system';
import { ModalSizes } from '@servicetitan/design-system/dist/components/Modal/components';
import { Divider } from '@material-ui/core';

interface CardSelectModalProps {
    cards: CreditCard[] | undefined;
    selectedCard: CreditCard;
    setSelectedCard: (selectedCard: CreditCard) => void;
    close: () => void;
    gotoNewCard: () => void;
    getCardIssuer: (issuer: CreditCardIssuer) => string;
    isOpen: boolean;
    gotoSavedCardPage: () => void;
    membershipNameType: string;
    isExpiringSoon: (year: number, month: number) => boolean;
}

export const CardSelectModal: React.FC<CardSelectModalProps> = ({
    cards,
    selectedCard,
    setSelectedCard,
    close,
    gotoNewCard,
    getCardIssuer,
    isOpen,
    gotoSavedCardPage,
    membershipNameType,
    isExpiringSoon,
}) => {
    const [replacedCard, setReplacedCard] = useState(selectedCard);
    const handleSave = () => {
        setSelectedCard(replacedCard);
        close();
    };
    const renderCard = (card: CreditCard, index: number) => (
        <div key={index}>
            {index > 0 && <Divider className={styles.divider} />}
            <Stack>
                <Stack.Item className={styles.radioColumn}>
                    {!card.isExpired && (
                        <Radio
                            value={card.id}
                            onClick={() => {
                                setReplacedCard(card);
                            }}
                            checked={replacedCard?.id === card.id}
                        />
                    )}
                </Stack.Item>
                <Stack.Item className={styles.cardItem}>
                    <Stack alignItems="center">
                        <Stack.Item className={styles.leftCard}>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                            >
                                {getCardIssuer(card.issuer)} ending in {card.number}
                            </BodyText>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                                subdued
                            >
                                Expires {card.expMonth.toString().padStart(2, '0')}/{card.expYear}
                            </BodyText>
                            <BodyText
                                size="medium"
                                className={classnames({ [styles.expired]: card.isExpired })}
                                subdued
                            >
                                {card.cardholderName}
                            </BodyText>
                        </Stack.Item>
                        <Stack.Item className={styles.rightCard}>
                            {card.primary && (
                                <Tag color="info" className="m-r-3 m-b-2" subtle>
                                    Primary Card
                                </Tag>
                            )}
                            {card.membership && (
                                <Tag color="info" className="m-r-3 m-b-2" subtle>
                                    {membershipNameType}
                                </Tag>
                            )}
                            {card.isExpired && (
                                <Tag color="critical" className="m-r-3 m-b-2" subtle>
                                    Expired
                                </Tag>
                            )}
                            {!card.isExpired && isExpiringSoon(card.expYear, card.expMonth) && (
                                <Tag color="warning" className="m-r-3 m-b-2" subtle>
                                    Expiring Soon
                                </Tag>
                            )}
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
            </Stack>
        </div>
    );

    return (
        <Modal
            size={ModalSizes.S}
            open={isOpen}
            onClose={close}
            title="Select A Different Card"
            footer={
                <React.Fragment>
                    <Button onClick={gotoNewCard}>Use New Card</Button>
                    <ButtonGroup>
                        <Button onClick={close}>Cancel</Button>
                        <Button primary onClick={handleSave}>
                            Save
                        </Button>
                    </ButtonGroup>
                </React.Fragment>
            }
            footerAlign="space-between"
        >
            <React.Fragment>
                <BodyText size="medium" className={styles.description}>
                    To manage your saved cards,{' '}
                    <Link color="primary" onClick={gotoSavedCardPage}>
                        click here.
                    </Link>
                </BodyText>
                <Card>{cards?.map((card, index) => renderCard(card, index))}</Card>
            </React.Fragment>
        </Modal>
    );
};
